'use client';

import Link from '@eliadmin/core/src/components/link';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { RiMenu2Line } from 'react-icons/ri';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { FiPhone } from 'react-icons/fi';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left text-xl font-semibold',
              layer === 1 && 'text-[16px] text-lg font-medium',
              layer && layer > 1 && 'text-sm font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label={t('text3241')}
          className={cn('text-black', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <span>
            <RiMenu2Line size={24} />
          </span>
        </SheetTrigger>
        <SheetContent
          side={'left'}
          aria-describedby={undefined}
          className={cn('', 'ea-mobile-sidebar__content', props.className?.content)}
          overlayClassName=" bg-black/80 backdrop-blur-none"
        >
          <SheetTitle className="sr-only">{t('text3241')}</SheetTitle>
          <div className="w-full">
            {!!props.menu && (
              <div className={cn('', 'ea-mobile-sidebar__menu')}>
                {props.menu?.map((item, index) => {
                  const Comp = item.path
                    ? isRelativeHref(getSafeHref(item.path))
                      ? Link
                      : 'a'
                    : 'span';
                  return (
                    <SheetClose key={index} asChild>
                      <Comp
                        href={item.path ? getSafeHref(item.path) : (undefined as any)}
                        className="flex w-full items-center justify-center"
                        prefetch={false}
                      >
                        <div
                          className={cn(
                            '',
                            'ea-mobile-sidebar__menu-item',
                            index === props?.menu?.length - 1 && 'hide-bottom-border',
                          )}
                        >
                          {item.name}
                        </div>
                      </Comp>
                    </SheetClose>
                  );
                })}
              </div>
            )}
          </div>
          <Button asChild className={cn('', 'ea-header-contact__button')}>
            <Link prefetchOnHover href={'/kontakt'}>
              <div className={cn('', 'ea-header-contact__icon')}>
                <FiPhone size={16} />
              </div>
              <p className={cn('', 'ea-header-contact__label')}>kontaktujte nás</p>
            </Link>
          </Button>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
